// Frontend Push-Subscription-Lifecycle
// =====================================
//
// Wave 14ca-fix7 — Stück 2.
//
// Verantwortlich für:
//   1. `subscribe()`        — Permission anfragen, PushSubscription erzeugen,
//                             POST an Backend
//   2. `unsubscribe()`      — lokale Subscription killen + DELETE ans Backend
//   3. `getCurrentState()`  — was ist der aktuelle Zustand (für UI-Toggle)
//   4. `isPushSupported()`  — Plattform-Capability-Check
//
// Permission-Lifecycle-Quirks die wir hier abfangen:
//   - iOS Safari: Push funktioniert NUR im Standalone-PWA-Mode, nicht im
//     normalen Safari-Tab (Apple-Politik).
//     `Notification.requestPermission()` muss aus User-Gesture-Kontext
//     gerufen werden (Tap-Handler, await-Chain ok).
//   - Browser ohne PushManager: Desktop Safari < 16.4, älteres Firefox-
//     mobile, alle FF-Forks etc. — `isPushSupported()` returnt dort false.
//   - Permission == 'denied': User hat aktiv abgelehnt. Browser prompted
//     nicht mehr automatisch; User muss das manuell in den Browser-
//     Settings reverten. Wir zeigen einen Hint.

const SUBSCRIBE_PATH = '/api/push/subscribe';
const UNSUBSCRIBE_PATH = '/api/push/unsubscribe';
const PUBLIC_KEY_PATH = '/api/push/public-key';

/**
 * Plattform-Check: serviceWorker, PushManager, Notification API alle drei
 * verfügbar? iOS Safari < 16.4 und Desktop Safari < 16.4 fallen hier raus.
 *
 * Auf iOS gilt zusätzlich: Push geht NUR im Standalone-Mode. Das checken
 * wir hier NICHT — die UI sitzt im InstallGate-after-Auth, ist also schon
 * standalone wenn die Permission-Karte zu sehen ist. Falls jemand den
 * InstallGate umgeht (z.B. via direkter URL im Safari-Tab nach Install),
 * gibt's halt einen Permission-Error den wir fail-soft anzeigen.
 */
export function isPushSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof navigator === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (typeof Notification === 'undefined') return false;
  return true;
}

export type PushPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

export type PushSubscribedState = {
  supported: boolean;
  permission: PushPermissionState;
  /** true = wir haben eine aktive PushSubscription beim Push-Service. */
  subscribed: boolean;
};

/**
 * Aktueller Zustand für UI. Synchron unsichere Promise weil
 * pushManager.getSubscription async ist.
 */
export async function getPushState(): Promise<PushSubscribedState> {
  if (!isPushSupported()) {
    return { supported: false, permission: 'unsupported', subscribed: false };
  }
  const permission = Notification.permission as PushPermissionState;
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    subscribed = sub !== null;
  } catch {
    /* SW noch nicht ready — egal, subscribed bleibt false */
  }
  return { supported: true, permission, subscribed };
}

/**
 * URL-Base64 → Uint8Array für `applicationServerKey`. PushManager.subscribe
 * verlangt einen ArrayBuffer, der VAPID-Public-Key kommt aber als URL-safe
 * Base64-String. Standard-atob klappt nicht 1:1 (- → +, _ → /, Padding).
 */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const safe = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(safe);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Vollständiger Subscribe-Flow:
 *   1. Permission anfragen (User-Gesture-Kontext)
 *   2. ServiceWorker-Registration holen
 *   3. PushManager.subscribe mit VAPID-Public-Key
 *   4. POST an Backend
 *
 * Wirft mit aussagekräftigen Fehler-Strings für die UI:
 *   - 'unsupported'
 *   - 'permission_denied'  → User hat 'denied' gewählt
 *   - 'no_public_key'      → Backend hat VAPID-Setup nicht gemacht
 *   - 'subscribe_failed'   → Browser/Push-Service-Fehler
 *   - 'backend_failed'     → POST /api/push/subscribe nicht 2xx
 */
export async function subscribe(): Promise<void> {
  if (!isPushSupported()) throw new Error('unsupported');

  // Permission anfragen. Nach 'granted' oder 'denied' muss der User es
  // manuell in Browser-Settings ändern, daher der UI-Hinweis im
  // PushPermissionCard wenn 'denied' kommt.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('permission_denied');
  }

  // VAPID-Public-Key: zuerst aus Build-Time-Env (sehr schnell), Fallback
  // auf Backend-Endpoint falls Env nicht gesetzt war.
  let publicKey =
    (import.meta.env.VITE_PUSH_VAPID_PUBLIC_KEY as string | undefined) || '';
  if (!publicKey) {
    try {
      const res = await fetch(PUBLIC_KEY_PATH, { credentials: 'include' });
      const data = (await res.json()) as { publicKey: string | null; enabled: boolean };
      if (data.enabled && data.publicKey) publicKey = data.publicKey;
    } catch {
      /* Network down oder Backend ohne Push — fail mit no_public_key */
    }
  }
  if (!publicKey) throw new Error('no_public_key');

  let subscription: PushSubscription;
  try {
    const reg = await navigator.serviceWorker.ready;
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Cast nötig: TypeScript-Lib-Defs verlangen BufferSource ohne
      // SharedArrayBuffer-Variante; unser Uint8Array<ArrayBufferLike>
      // erfüllt das zur Laufzeit, der Compiler sieht es aber strenger.
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  } catch (err) {
    console.warn('[push] pushManager.subscribe failed', err);
    throw new Error('subscribe_failed');
  }

  // Subscription als JSON ans Backend. credentials: 'include' damit der
  // Cookie mitgeht (sonst 401).
  const body = {
    subscription: subscription.toJSON(),
    userAgent: navigator.userAgent,
  };
  const res = await fetch(SUBSCRIBE_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // Lokale Subscription wieder rauslöschen — sonst hängt eine ungespeicherte
    // Push-Subscription beim Browser, die wir nie wiederherstellen können.
    try {
      await subscription.unsubscribe();
    } catch {
      /* best effort */
    }
    throw new Error('backend_failed');
  }
}

/**
 * Unsubscribe-Flow: lokale PushSubscription killen + Backend informieren.
 * Idempotent — wenn nichts existiert, kein Fehler.
 */
export async function unsubscribe(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    // Backend bestmöglich informieren — wenn das fehlschlägt (offline, …),
    // räumt der nächste sendNotification-Pass auf (410 → prune).
    try {
      await fetch(UNSUBSCRIBE_PATH, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ endpoint }),
      });
    } catch {
      /* offline — ok */
    }
  } catch (err) {
    console.warn('[push] unsubscribe failed', err);
  }
}
